import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react';
import './draggable-entity-card.css';
import {
	ENTITY_FA_ICON_NAMES,
	ENTITY_COLORS,
	getCanvasEntityDOMId,
	resolveEntityColorClass,
	fromDisabledReasonsToCardState
} from '../../services/editor/ui';
import Button from '../../lib/button/Button';
import { Canvas } from '../../lib/canvas';
import {
	ChangeSet,
	InstalledProjectState,
	lowercaseFirstLetter,
	ProjectState,
	CanvasEntityConnectionDisabledReason,
	CanvasEntityDisabledReason,
	CanvasEntityOtherDisabledReason,
	CanvasEntityTestingDisabledReason,
	ALL_CONNECTION_DISABLED_REASONS,
	addDisabledReasons,
	removeDisabledReasons,
	READABLE_ENTITY_TYPES,
	StateMutationAction,
	safeResolveEntityName
} from '@elyx-code/project-logic-tree';
import { ICanvasElementComponentProps } from '../../services/editor/CanvasElement';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import CanvasPopup, { useCanvasPopup } from '../../services/editor/CanvasPopup';
import CanvasDropdownMenuCard from '../../lib/dropdown/CanvasDropdownMenuCard';
import { popupNotification } from '../../popup-notification';

interface IDraggableEntityCardProps extends ICanvasElementComponentProps {
	entity: InstalledProjectState;
}

const DraggableEntityCard = (props: IDraggableEntityCardProps) => {
	const rootElementRef = useRef(null);

	useEffect(() => {
		const element = rootElementRef.current;

		if (!!element && document.contains(element)) {
			props.onMount?.(element);
		}

		// When the component is unmounted
		return () => {
			props.onUnmount?.();
		};
	}, []);

	useEffect(() => {
		const element = rootElementRef.current;

		props.onUpdate?.(element);
	});

	useLayoutEffect(() => {
		props.onBeforeUpdate?.();
	}, []);

	const [disabledReasons, setDisabledReasons] = useState<
		CanvasEntityDisabledReason[]
	>([]);

	const [updateKey, setValue] = useState(0);

	const useForceUpdate = () => {
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	const memoizedCanvasPopupBody = useCallback(
		(popup: CanvasPopup) => (
			<CanvasPopupBaseComponent
				id={
					'canvas-draggable-other-card-menu-button-' +
					props.entity.id +
					'--popup--body'
				}
			>
				<CanvasDropdownMenuCard
					options={[
						{
							project: props.project,
							onClick: async () => {
								props.project.canvas?.removeDraggableObject(
									props.canvasElement.draggableObject
								);
							},
							label: 'Delete',
							color: 'error',
							iconEnd: 'fa-solid fa-trash-can',
							disabled: !entity.deletable,
							tooltip: entity.deletable
								? 'Forever delete this node'
								: 'This node cannot be deleted. Because the system need to refer to it over time.',
							parentCanvasObject: popup.self,
							id:
								'canvas-draggable-other-card--' +
								props.entity.id +
								'--delete'
						},
						// {
						// 	onClick: async () => {},
						// 	label: 'Edit',
						// 	iconEnd: 'fa-solid fa-pencil',
						// 	disabled: false,
						// 	tooltip: !entity.editable ? "This node has been locked and can no longer be edited" : "Edit this node",
						// 	parentCanvasObject: popup.self,
						// 	id: 'canvas-draggable-other-card--' + props.entity.id + '--edit',
						// },
						{
							project: props.project,
							onClick: async () => {
								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											entity,
											false,
											StateMutationAction.RecalculateNode
										)
									);

								entity.afterAllChildrenInitialized(changeSet);

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							},
							label: 'Recalculate',
							color: 'warning',
							// Bug icon
							iconEnd: 'fa-solid fa-bug',
							disabled: false,
							// Lower case the first letter of the resolved readable name
							tooltip:
								'Recalculate the state of this ' +
								lowercaseFirstLetter(
									READABLE_ENTITY_TYPES[props.entity.type]
										.shortSingular
								),
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--recalculate'
						},
						{
							project: props.project,
							onClick: async () => {
								navigator.clipboard.writeText(props.entity.id);
								popupNotification({
									text: `Copied ID: ${props.entity.id}`,
									color: 'success'
								});
							},
							label: 'Copy ID',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								'Copy this node ID to clipboard for debugging',
							parentCanvasObject: popup.self,
							id: 'copy-id-' + props.entity.id
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.recalculateValidateEntity(
									props.entity
								);
								popupNotification({
									text: `Re-validated node successfully`,
									color: 'success'
								});
							},
							label: 'Re-validate',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								"Re-validate this node's state and its errors",
							parentCanvasObject: popup.self,
							id: 're-validate-' + props.entity.id
						}
					]}
				/>
			</CanvasPopupBaseComponent>
		),
		[
			props.entity,
			props.project,
			props.canvasElement,
			// @ts-ignore
			props.entity.suggestion
		]
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id:
				'canvas-draggable-other-card-menu-button-' +
				props.entity.id +
				'--popup',
			anchorQuerySelector:
				'#canvas-draggable-other-card-menu-button-' + props.entity.id,
			arrowClasses: ['dropdown-menu-card-arrow'],
			parentCanvasObject: props.canvasElement.draggableObject,
			arrowContainerSelector:
				'#canvas-draggable-other-card-menu-button-' +
				props.entity.id +
				'--popup--body'
		},
		memoizedCanvasPopupBody
	);

	useEffect(() => {
		// --- Initial Setup Logic ---
		const initialNewDisabledReasons: CanvasEntityOtherDisabledReason[] = [];

		// @ts-ignore
		if (props.entity.suggestion) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.Suggestion
			);
		}

		if (props.project.canvas?.disabled) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
			);
		}

		if (!!initialNewDisabledReasons.length) {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					initialNewDisabledReasons
				);
				return config.reasons;
			});
		} else {
			// This handles the case where canvas was disabled but is now enabled
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		}

		const handleCanvasGloballyEnabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};
		const handleCanvasGloballyDisabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};

		const handleEntityUpdate = (payload: any) => {
			let hasUpdated = false;
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];
			const disabledReasonsToRemove: CanvasEntityTestingDisabledReason[] =
				[];

			props.project.test?.disabledConnectionsNotInTest(
				props.canvasElement.draggableObject,
				// @ts-ignore
				props.entity
			);

			if (
				!!props.project.test &&
				// @ts-ignore
				!props.project.test?.hasEntity(props.entity) &&
				// @ts-ignore
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
				hasUpdated = true; // Mark as updated
			}

			if (
				// @ts-ignore
				(props.project.test?.hasEntity(props.entity) ||
					// @ts-ignore
					props.project.test?.getCanBeAddedToTest(props.entity)) &&
				props.canvasElement.draggableObject.disabled
			) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				disabledReasonsToRemove.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
				hasUpdated = true;
			}

			if (
				!!props.project.test &&
				// @ts-ignore
				!props.project.test?.hasEntity(props.entity)
			) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
				hasUpdated = true;
			}

			if (newDisabledReasons.length || disabledReasonsToRemove.length) {
				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newDisabledReasons
					);
					const finalConfig = removeDisabledReasons(
						config.reasons,
						disabledReasonsToRemove
					);
					return finalConfig.reasons;
				});
				// We already set hasUpdated, so no need to set it again
			}

			if (hasUpdated) {
				forceUpdate(); // This is fine if hasUpdated is true
			} else {
				forceUpdate(); // This was in your original logic
			}
		};

		const handleTestSetupStarted = () => {
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];

			if (
				!!props.project.test &&
				// @ts-ignore
				!props.project.test?.hasEntity(props.entity) &&
				// @ts-ignore
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				props.project.test?.disabledConnectionsNotInTest(
					props.canvasElement.draggableObject,
					// @ts-ignore
					props.entity
				);

				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
			}

			// @ts-ignore
			if (!props.project.test?.hasEntity(props.entity)) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
			}

			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					newDisabledReasons
				);
				return config.reasons;
			});
		};

		const handleExecutionStart = () => {
			// @ts-ignore
			if (!!props.project.test?.hasEntity(props.entity)) {
				forceUpdate();
			}
		};

		const handleTestExited = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest,
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				]);
				return config.reasons;
			});

			if (props.canvasElement.draggableObject.disabled) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
			}

			props.canvasElement.draggableObject.connections.forEach(
				(connection) => {
					connection.enable();
					connection.element.classList.remove('disabled');

					if (connection.complementaryLineElement) {
						connection.complementaryLineElement.classList.remove(
							'disabled'
						);
					}
				}
			);
		};

		const handleCanvasConnectionStart = (payload: any) => {
			if (!payload.compatible?.[props.entity.id]) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);

				const newOwnConnectionDisabledReasons: CanvasEntityConnectionDisabledReason[] =
					payload.incompatible?.[props.entity.id]?.reasons || [];

				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newOwnConnectionDisabledReasons
					);
					return config.reasons;
				});
			} else {
				forceUpdate();
			}
		};

		const handleCanvasConnectionEnd = () => {
			props.project.canvas?.enable(props.canvasElement.draggableObject, {
				quiet: true
			});

			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(
					currentReasons,
					ALL_CONNECTION_DISABLED_REASONS
				);
				return config.reasons;
			});
		};

		props.project.on(
			'canvas-globally-enabled',
			handleCanvasGloballyEnabled
		);
		props.project.on(
			'canvas-globally-disabled',
			handleCanvasGloballyDisabled
		);
		props.project.on(props.entity.id, handleEntityUpdate);
		props.project.on('test-setup-started', handleTestSetupStarted);
		props.project.on('execution-start', handleExecutionStart);
		props.project.on('test-exited', handleTestExited);
		props.project.on(
			'canvas-connection-start',
			handleCanvasConnectionStart
		);
		props.project.on('canvas-connection-end', handleCanvasConnectionEnd);

		return () => {
			props.project.off(
				'canvas-globally-enabled',
				handleCanvasGloballyEnabled
			);
			props.project.off(
				'canvas-globally-disabled',
				handleCanvasGloballyDisabled
			);
			props.project.off(props.entity.id, handleEntityUpdate);
			props.project.off('test-setup-started', handleTestSetupStarted);
			props.project.off('execution-start', handleExecutionStart);
			props.project.off('test-exited', handleTestExited);
			props.project.off(
				'canvas-connection-start',
				handleCanvasConnectionStart
			);
			props.project.off(
				'canvas-connection-end',
				handleCanvasConnectionEnd
			);
		};
	}, [
		props.project,
		props.entity,
		props.canvasElement,
		updateKey,
		// @ts-ignore
		props.entity.suggestion
	]);

	useEffect(() => {
		props.canvasElement.draggableObject.updateConnections();
	});

	const entity = props.entity;
	const entityColor = ENTITY_COLORS[entity.type];

	return (
		<div
			ref={rootElementRef}
			className={['canvas-draggable-card'].join(' ')}
			id={getCanvasEntityDOMId(props.entity)}
			{...{
				[Canvas.OBJ_ATTR_NAME]: getCanvasEntityDOMId(props.entity)
			}}
			onClick={(e) => {
				e.stopPropagation();
			}}
		>
			<div
				className={[
					'canvas-draggable-card__body',
					...(!!disabledReasons.length
						? ['canvas-draggable-card__body__disabled']
						: []),
					resolveEntityColorClass(props.entity.type, {
						state: fromDisabledReasonsToCardState(disabledReasons)
					})
				].join(' ')}
			>
				<header
					className={[
						'canvas-draggable-card__header',
						...(!!disabledReasons.length ? ['disabled'] : [])
					].join(' ')}
					{...{
						[Canvas.OBJ_ATTR_NAME]:
							getCanvasEntityDOMId(props.entity) + '--header'
					}}
					style={{ backgroundColor: entityColor }}
				>
					<div className="canvas-draggable-card__header__left-block">
						<i
							className={`canvas-draggable-card-header__icon fa-solid fa-${
								ENTITY_FA_ICON_NAMES[entity.type] ||
								'rectangle-list'
							}`}
						/>
						<span className="canvas-draggable-card__header__labels">
							<h3>
								{safeResolveEntityName(
									entity,
									props.project.logic
								)}
							</h3>
							<p>
								{READABLE_ENTITY_TYPES[entity.type]?.singular}
							</p>
						</span>
					</div>
					<div className="canvas-draggable-card__header__actions">
						{/* Three dots menu */}
						<Button
							variant="text"
							color="grey"
							iconEnd="fa-solid fa-ellipsis-v"
							size="small"
							className="canvas-small-entity-card-menu-button"
							id={`canvas-small-entity-card-menu-button-${entity.id}`}
							disabled={!!disabledReasons.length}
							ref={canvasPopup.anchorRef}
							onClick={(e: any) => {
								e.stopPropagation();
								canvasPopup?.toggle();
							}}
						/>
					</div>
				</header>
			</div>
		</div>
	);
};

export default DraggableEntityCard;
